/**
 * Council profile page.
 *
 * What lives here (council surface):
 *   - Basics: display name + email (read-only from SSO), bio, GitHub
 *   - Directory profile: Entra-synced job/department/company/country/office
 *   - Assigned FA: read-only badge in the sidebar
 *
 *   - Talent pool: the "Available as talent" opt-in (ticket #13)
 *   - Skills & interests: the shared ProfileSkills editor. It was left out
 *     while council were not matched against demands, but skills imported
 *     from the teams' sheet (2026-10-12) reached council members too, and
 *     they had no page to see or change them on. The "Notify me" bell shows
 *     only once they are in the talent pool, the only people it mails.
 *
 * What's NOT here (lives in TalentProfile.tsx):
 *   - "Visible in directory" toggle — only meaningful for talent
 *   - Preferred FA dropdown — council have an assigned FA, not a
 *     preference
 *
 * The talent opt-in IS here, though the rest of the talent surface is not.
 * `talent_profile_active` is the single flag behind both the Talent directory
 * and the "a position was posted" email, and both queries are role-agnostic —
 * an admin carrying the flag appears in the directory, and a backend test pins
 * that. What was missing was any way for a non-talent to SET it: this page
 * omitted the switch and `AdminUserUpdate` did not carry the field either, so
 * the only routes were a hand-rolled PUT or SQL. That is ticket #13.
 *
 * Admin / super_admin land here too — they're "council level" and the
 * council shape is appropriate for any cross-FA admin role.
 *
 * Split from the unified Profile.tsx so the council surface can diverge
 * from talent without conditional gating. Sibling file is
 * TalentProfile.tsx.
 */

import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Caption1,
  Input,
  Spinner,
  Subtitle2,
  Switch,
  Text,
  Textarea,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  Building20Regular,
  Globe20Regular,
  Location20Regular,
  Organization20Regular,
  Save20Regular,
} from '@fluentui/react-icons'
import { useAuth } from '../../context/AuthContext'
import { useEffectiveUser } from '../../hooks/useEffectiveUser'
import { ProfileSkills } from '../../components/ProfileSkills'
import { useToast } from '../../components/ToastProvider'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useUpdateProfile } from '../../hooks/useProfile'
import { bayerColors } from '../../theme'
import { apiErrorMessage } from '../../lib/apiError'
import { githubHandleProblem } from '../../lib/githubHandle'
import { TitleWithHelp } from '../../components/TitleWithHelp'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '1080px',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  headTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    gap: tokens.spacingHorizontalXL,
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  side: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
    background: '#FFFFFF',
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow2,
  },
  avatar: {
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    background: bayerColors.navy,
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    background: '#FFFFFF',
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow2,
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  fieldHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  readonlyInput: {
    background: tokens.colorNeutralBackground3,
    cursor: 'not-allowed',
  },
  dirRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  dirRowLast: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
  },
  dirIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  dirContent: { display: 'flex', flexDirection: 'column', gap: '1px' },
  dirLabel: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  dirValue: { fontSize: '14px', color: tokens.colorNeutralForeground1 },
  dirEmpty: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic' as const,
  },
})

function initials(name: string | null | undefined) {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => (p[0] ?? '').toUpperCase())
    .join('')
}

export function CouncilProfile() {
  useDocumentTitle('My profile')
  const styles = useStyles()
  const { user } = useAuth()
  const effectiveUser = useEffectiveUser()
  const { showToast } = useToast()

  // Source drafts + dirty checks from effectiveUser so impersonation
  // surfaces the impersonated user's data, not the real admin's.
  const [bioDraft, setBioDraft] = useState<string>(effectiveUser?.bio ?? '')
  const [githubDraft, setGithubDraft] = useState<string>(effectiveUser?.github_handle ?? '')
  const updateProfile = useUpdateProfile()
  // Reset drafts when the impersonated user changes mid-session.
  useEffect(() => {
    setBioDraft(effectiveUser?.bio ?? '')
    setGithubDraft(effectiveUser?.github_handle ?? '')
  }, [effectiveUser?.id, effectiveUser?.bio, effectiveUser?.github_handle])
  // Mirrors TalentProfile's handling: the switch saves on flip rather than
  // waiting for "Save changes", because it is a single boolean with no partner
  // field to batch with.
  const [talentDraft, setTalentDraft] = useState<boolean>(
    effectiveUser?.talent_profile_active ?? false,
  )
  useEffect(() => {
    setTalentDraft(effectiveUser?.talent_profile_active ?? false)
  }, [effectiveUser?.id, effectiveUser?.talent_profile_active])

  const handleTalentToggle = (_: unknown, d: { checked: boolean }) => {
    setTalentDraft(d.checked)
    updateProfile.mutate(
      { talent_profile_active: d.checked },
      {
        onError: (err) => {
          setTalentDraft(!d.checked)
          showToast({
            title: 'Could not update talent profile',
            body: apiErrorMessage(err),
            intent: 'error',
          })
        },
      },
    )
  }

  const bioDirty = (effectiveUser?.bio ?? '') !== bioDraft.trim()
  const githubDirty = (effectiveUser?.github_handle ?? '') !== githubDraft.trim()
  const anyDirty = bioDirty || githubDirty
  const githubProblem = githubHandleProblem(githubDraft)
  const isImpersonating = effectiveUser?.id !== user?.id

  const saveBasics = () => {
    if (!anyDirty || githubProblem) return
    const patch: { bio?: string | null; github_handle?: string | null } = {}
    if (bioDirty) patch.bio = bioDraft.trim() || null
    if (githubDirty) patch.github_handle = githubDraft.trim() || null
    updateProfile.mutate(patch, {
      onSuccess: () => showToast({ title: 'Profile saved', intent: 'success' }),
      onError: (err) =>
        showToast({ title: 'Could not save profile', body: apiErrorMessage(err), intent: 'error' }),
    })
  }

  const faLabel =
    effectiveUser?.role === 'council'
      ? 'Assigned focus area'
      : effectiveUser?.role === 'admin' || effectiveUser?.role === 'super_admin'
        ? 'Focus area (admin)'
        : 'Focus area'

  return (
    <div className={styles.root}>
      <header className={styles.head}>
        <div className={styles.headTitleBlock}>
          <TitleWithHelp topic="profile">
            <Title2 as="h1">My profile</Title2>
          </TitleWithHelp>
        </div>
      </header>

      <div className={styles.grid}>
        {/* Side: identity + assigned FA badge */}
        <aside className={styles.side} aria-label="Identity">
          <div className={styles.avatar} aria-hidden="true">
            {initials(effectiveUser?.name)}
          </div>
          <Subtitle2 as="h2" style={{ textAlign: 'center' }}>
            {effectiveUser?.name ?? '—'}
          </Subtitle2>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, textAlign: 'center' }}>
            {effectiveUser?.email ?? ''}
          </Caption1>

          {effectiveUser?.focus_area_code && (
            <div style={{ width: '100%', textAlign: 'center' }}>
              <Caption1
                style={{
                  display: 'block',
                  color: tokens.colorNeutralForeground3,
                  marginBottom: tokens.spacingVerticalXXS,
                }}
              >
                {faLabel}
              </Caption1>
              <Badge appearance="tint" color="informative">
                {effectiveUser.focus_area_code}
              </Badge>
            </div>
          )}

          {isImpersonating && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              Changes apply to the impersonated user&apos;s profile.
            </Caption1>
          )}
        </aside>

        {/* Main column */}
        <div className={styles.main}>
          {/* Basics */}
          <section className={styles.section} aria-labelledby="profile-basics-h">
            <div className={styles.sectionHeader}>
              <Subtitle2 id="profile-basics-h" as="h2">
                Basics
              </Subtitle2>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="profile-name">
                Display name
              </label>
              <Input
                id="profile-name"
                value={effectiveUser?.name ?? ''}
                readOnly
                input={{ className: styles.readonlyInput }}
                aria-label="Display name"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="profile-email">
                Work email
              </label>
              <Input
                id="profile-email"
                value={effectiveUser?.email ?? ''}
                readOnly
                input={{ className: styles.readonlyInput }}
                aria-label="Work email"
              />
              <span className={styles.fieldHint}>
                Name and email come from Bayer SSO.
              </span>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="profile-bio">
                About
              </label>
              <Textarea
                id="profile-bio"
                value={bioDraft}
                onChange={(_, d) => setBioDraft(d.value)}
                rows={3}
                resize="vertical"
                placeholder="Your focus within the council"
                aria-label="About"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="profile-github">
                GitHub
              </label>
              <Input
                id="profile-github"
                value={githubDraft}
                onChange={(_, d) => setGithubDraft(d.value)}
                placeholder="your-username"
                aria-label="GitHub handle"
                contentBefore={
                  <span style={{ color: tokens.colorNeutralForeground3 }}>@</span>
                }
                aria-invalid={!!githubProblem}
                aria-describedby={githubProblem ? 'profile-github-problem' : undefined}
              />
              {githubProblem && (
                <span
                  id="profile-github-problem"
                  style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }}
                >
                  {githubProblem}
                </span>
              )}
            </div>

            <div>
              <Button
                appearance="primary"
                icon={updateProfile.isPending ? <Spinner size="tiny" /> : <Save20Regular />}
                onClick={saveBasics}
                disabled={!anyDirty || !!githubProblem || updateProfile.isPending}
              >
                {updateProfile.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </section>

          {/* Talent pool opt-in (ticket #13) */}
          <section className={styles.section} aria-labelledby="profile-talent-h">
            <div className={styles.sectionHeader}>
              <Subtitle2 id="profile-talent-h" as="h2">
                Talent pool
              </Subtitle2>
            </div>

            <div className={styles.field}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: tokens.fontSizeBase300 }}>
                    Available as talent
                  </div>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    Lets you apply, appear in the Talent directory, and hear about positions.
                  </Caption1>
                </div>
                <Switch
                  aria-label="Available as talent"
                  checked={talentDraft}
                  onChange={handleTalentToggle}
                />
              </div>
              {talentDraft && (
                <Caption1 className={styles.fieldHint}>
                  Postings in every focus area will email you. Narrow it on the Talent profile.
                </Caption1>
              )}
            </div>
          </section>

          {/* Skills */}
          <section className={styles.section} aria-labelledby="profile-skills-h">
            <div className={styles.sectionHeader}>
              <Subtitle2 id="profile-skills-h" as="h2">
                Skills &amp; interests
              </Subtitle2>
            </div>
            <ProfileSkills notify={talentDraft} />
          </section>

          {/* Directory profile (read-only from MS Entra) */}
          <section className={styles.section} aria-labelledby="profile-dir-h">
            <div className={styles.sectionHeader}>
              <Subtitle2 id="profile-dir-h" as="h2">
                Directory profile
              </Subtitle2>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                From Microsoft Entra; IT can change it.
              </Text>
            </div>

            {[
              { icon: <Organization20Regular />, label: 'Job title', value: effectiveUser?.job_title },
              { icon: <Building20Regular />, label: 'Department', value: effectiveUser?.department },
              { icon: <Building20Regular />, label: 'Company', value: effectiveUser?.company },
              { icon: <Globe20Regular />, label: 'Country', value: effectiveUser?.country },
              { icon: <Location20Regular />, label: 'Office', value: effectiveUser?.office_location },
            ].map((f, i, arr) => (
              <div
                key={f.label}
                className={i < arr.length - 1 ? styles.dirRow : styles.dirRowLast}
              >
                <span className={styles.dirIcon}>{f.icon}</span>
                <div className={styles.dirContent}>
                  <Caption1 className={styles.dirLabel}>{f.label}</Caption1>
                  {f.value ? (
                    <Text className={styles.dirValue}>{f.value}</Text>
                  ) : (
                    <Text className={styles.dirEmpty}>Not set in directory</Text>
                  )}
                </div>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  )
}
